
import React, { useState, useEffect } from 'react';
import { CustomerLayout } from '../../components/ui/Layout';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { 
  MapPin, CreditCard, Wallet, ShieldCheck, ShoppingBag, 
  Banknote, Check, ArrowRight, Sparkles, Trash2, Minus, Plus, Smartphone, Zap, Shield
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { items, cartTotal, placeOrder, loyaltyReward, removeFromCart, updateQuantity, isProcessing } = useCart();
  const { restaurant } = useStore();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    method: '', 
    changeFor: ''
  });

  useEffect(() => {
    if (restaurant) {
      if (restaurant.payment.acceptStripe && restaurant.stripeOnboardingComplete) {
        setFormData(prev => ({ ...prev, method: 'STRIPE' }));
      } else if (restaurant.payment.acceptCard) {
        setFormData(prev => ({ ...prev, method: 'CARD_ON_DELIVERY' }));
      } else if (restaurant.payment.acceptCash) {
        setFormData(prev => ({ ...prev, method: 'CASH' }));
      }
    }
  }, [restaurant]);

  useEffect(() => {
    if (user) {
        setFormData(prev => ({
            ...prev,
            name: user.user_metadata?.full_name || prev.name,
            phone: user.user_metadata?.phone || prev.phone
        }));
    }
  }, [user]);

  const isFreeDelivery = (restaurant?.delivery?.minOrderForFreeDelivery && cartTotal >= restaurant?.delivery?.minOrderForFreeDelivery);
  const deliveryFee = isFreeDelivery ? 0 : (restaurant?.delivery?.fee || 0);
  const finalTotal = cartTotal + deliveryFee;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restaurant) return;
    
    if (cartTotal < (restaurant.minOrderValue || 0)) {
        addToast(`Pedido mínimo: R$ ${restaurant.minOrderValue?.toFixed(2)}`, 'error');
        return;
    }

    if (!formData.method) {
      addToast('Selecione uma forma de pagamento.', 'error');
      return;
    }

    try {
      const result = await placeOrder(formData, 0);
      
      if (typeof result === 'object' && 'checkoutUrl' in result) {
        addToast('Redirecionando para pagamento...', 'info');
        console.log("Abrindo Stripe Checkout:", result.checkoutUrl);
        window.location.assign(result.checkoutUrl);
      } else {
        addToast('Pedido enviado com sucesso!', 'success');
        navigate(`/${restaurant.slug}/track/${result}`);
      }
    } catch (error: any) {
      console.error("Falha no checkout:", error);
      addToast(error.message || 'Erro ao processar gateway. Tente outro método.', 'error');
    }
  };

  if (!restaurant) return null;

  if (items.length === 0) {
    return (
        <CustomerLayout showCart={false}>
             <div className="flex flex-col items-center justify-center h-[70vh] text-center px-6 animate-fade-in">
                <div className="w-32 h-32 bg-gray-100 rounded-[3rem] flex items-center justify-center mb-8">
                   <ShoppingBag className="w-12 h-12 text-gray-300" />
                </div>
                <h2 className="text-5xl font-black italic uppercase tracking-tighter mb-6">SACOLA VAZIA.</h2>
                <button onClick={() => navigate(`/${restaurant.slug}/menu`)} className="bg-gray-900 text-white px-12 py-5 rounded-full font-black text-xs uppercase tracking-widest hover:bg-rose-500 transition-all">Explorar Cardápio</button>
             </div>
        </CustomerLayout>
    );
  }

  return (
    <CustomerLayout showCart={false}>
      {/* Restante da UI permanece inalterado... */}
      <div className="px-6 md:px-0 pb-64 max-w-6xl mx-auto animate-fade-in">
        <header className="pt-20 mb-16 text-center lg:text-left">
             <h1 className="text-7xl md:text-8xl font-black italic tracking-tighter uppercase leading-none">FECHAR <br/> <span className="text-rose-500">PEDIDO.</span></h1>
        </header>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
           <div className="lg:col-span-7 space-y-12">
              <section className="bg-white border border-gray-100 rounded-[3rem] p-10 space-y-8 shadow-premium">
                 <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] flex items-center gap-4">
                   <ShoppingBag className="w-5 h-5 text-rose-500" /> Revisar Sacola
                 </h3>
                 <div className="divide-y divide-gray-50">
                    {items.map(item => (
                       <div key={item.internalId} className="py-6 flex items-center justify-between group">
                          <div className="flex items-center gap-6">
                             <img src={item.imageUrl} className="w-16 h-16 rounded-2xl object-cover border border-gray-100 shadow-sm" alt={item.productName} />
                             <div>
                                <h4 className="font-black text-gray-900 uppercase italic text-sm">{item.productName}</h4>
                                <div className="flex items-center gap-4 mt-2">
                                   <div className="flex items-center bg-gray-50 rounded-xl p-1 border border-gray-100">
                                      <button type="button" onClick={() => updateQuantity(item.internalId, -1)} className="p-1 hover:text-rose-500 transition-colors"><Minus className="w-3.5 h-3.5" /></button>
                                      <span className="w-8 text-center text-[11px] font-black">{item.quantity}</span>
                                      <button type="button" onClick={() => updateQuantity(item.internalId, 1)} className="p-1 hover:text-rose-500 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                                   </div>
                                   <span className="text-xs font-bold text-gray-400">R$ {item.totalPrice.toFixed(2)}</span>
                                </div>
                             </div>
                          </div>
                       </div>
                    ))}
                 </div>
              </section>

              <section className="bg-white border border-gray-100 rounded-[3rem] p-10 space-y-10 shadow-premium">
                 <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] flex items-center gap-4">
                   <MapPin className="w-5 h-5 text-rose-500" /> Informações de Entrega
                 </h3>
                 <div className="space-y-6">
                    <InputGroup label="Seu Nome" value={formData.name} onChange={(v: string) => setFormData({...formData, name: v})} placeholder="Ex: João Silva" />
                    <InputGroup label="WhatsApp para Contato" value={formData.phone} onChange={(v: string) => setFormData({...formData, phone: v})} placeholder="(11) 99999-9999" />
                    <InputGroup label="Endereço de Entrega" value={formData.address} onChange={(v: string) => setFormData({...formData, address: v})} placeholder="Rua, número, bairro e referência" />
                 </div>
              </section>

              <section className="bg-white border border-gray-100 rounded-[3rem] p-10 space-y-10 shadow-premium">
                 <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] flex items-center gap-4">
                   <Shield className="w-5 h-5 text-rose-500" /> Fluxil Pay Gateway
                 </h3>
                 <div className="grid grid-cols-1 gap-4">
                    {restaurant.payment.acceptStripe && restaurant.stripeOnboardingComplete && (
                      <PaymentOption 
                          active={formData.method === 'STRIPE'} 
                          onClick={() => setFormData({...formData, method: 'STRIPE'})} 
                          icon={Zap} 
                          label="Fluxil Pay (Cartão ou Pix)" 
                          desc="Pagamento online seguro e instantâneo" 
                          badge="GATEWAY ATIVO"
                      />
                    )}

                    {restaurant.payment.acceptCard && (
                      <PaymentOption 
                          active={formData.method === 'CARD_ON_DELIVERY'} 
                          onClick={() => setFormData({...formData, method: 'CARD_ON_DELIVERY'})} 
                          icon={Smartphone} 
                          label="Máquina (Na Entrega)" 
                          desc="Cartões de crédito/débito" 
                      />
                    )}

                    {restaurant.payment.acceptCash && (
                      <PaymentOption 
                          active={formData.method === 'CASH'} 
                          onClick={() => setFormData({...formData, method: 'CASH'})} 
                          icon={Banknote} 
                          label="Dinheiro" 
                          desc="Pagamento em espécie na entrega" 
                      />
                    )}
                 </div>
              </section>
           </div>

           <div className="lg:col-span-5">
              <div className="sticky top-32 bg-gray-900 text-white rounded-[4rem] p-12 shadow-2xl space-y-10 border border-white/5 overflow-hidden">
                 <h3 className="text-3xl font-black italic uppercase tracking-tighter">Resumo do Pedido</h3>
                 <div className="space-y-5">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-gray-500">
                       <span>Subtotal</span>
                       <span className="text-white">R$ {cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
                       <span>Entrega</span>
                       <span className={deliveryFee === 0 ? 'text-green-500 font-black' : 'text-white'}>{deliveryFee === 0 ? 'GRÁTIS' : `R$ ${deliveryFee.toFixed(2)}`}</span>
                    </div>
                    <div className="pt-8 border-t border-white/10">
                        <p className="text-[9px] font-black uppercase tracking-[0.4em] text-gray-500 mb-2">Total a Pagar</p>
                        <p className="text-6xl font-black tracking-tighter italic text-rose-500">R$ {finalTotal.toFixed(2).replace('.', ',')}</p>
                    </div>
                 </div>
                 
                 <button 
                   type="submit" 
                   disabled={isProcessing || !formData.method}
                   className="w-full bg-white text-black h-24 rounded-[2.5rem] font-black text-sm uppercase tracking-widest flex items-center justify-center gap-4 hover:bg-rose-500 hover:text-white transition-all disabled:opacity-50 shadow-glow-rose"
                 >
                    {isProcessing ? <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div> : <>CONFIRMAR PEDIDO <ArrowRight className="w-6 h-6" /></>}
                 </button>
              </div>
           </div>
        </form>
      </div>
    </CustomerLayout>
  );
};

const InputGroup = ({ label, value, onChange, placeholder }: any) => (
  <div className="space-y-3">
    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-2">{label}</label>
    <input required placeholder={placeholder} className="w-full bg-gray-50 border border-gray-100 rounded-3xl px-8 py-5 text-gray-900 font-bold outline-none focus:bg-white transition-all shadow-inner" value={value} onChange={e => onChange(e.target.value)} />
  </div>
);

const PaymentOption = ({ active, onClick, icon: Icon, label, desc, badge }: any) => (
  <button type="button" onClick={onClick} className={`p-8 rounded-[3rem] border-2 flex items-center justify-between transition-all text-left relative ${active ? 'border-rose-500 bg-rose-500/5 shadow-premium' : 'border-gray-50 bg-gray-50 hover:bg-gray-100'}`}>
    {badge && <span className="absolute -top-3 left-10 bg-rose-500 text-white text-[8px] font-black px-4 py-1.5 rounded-full shadow-lg">{badge}</span>}
    <div className="flex items-center gap-8">
       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${active ? 'bg-rose-600 text-white shadow-glow-rose rotate-3' : 'bg-white text-gray-300 border border-gray-100'}`}><Icon className="w-7 h-7" /></div>
       <div>
          <span className={`block font-black text-sm uppercase tracking-widest ${active ? 'text-gray-900' : 'text-gray-500'}`}>{label}</span>
          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{desc}</span>
       </div>
    </div>
    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${active ? 'border-rose-500 bg-rose-500' : 'border-gray-200 bg-white'}`}>{active && <Check className="w-4 h-4 text-white" />}</div>
  </button>
);

export default CheckoutPage;
